import { PhotographIcon } from '@heroicons/react/outline';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay, tone } from '../../primitives';

/**
 * The image-attachments switch from the agent's Appearance settings, and a
 * customer's photo of a rating plate that the agent reads to answer.
 */
export default function ImageQuestion({
  brand,
  question,
  answer,
}: {
  brand: DemoBrand;
  question: string;
  answer: string;
}) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[30rem] space-y-4'>
      <div
        className={clsxm(
          'flex items-center gap-3.5 rounded-2xl p-4 text-start shadow-[0_30px_60px_-28px_rgba(0,0,0,0.65)]',
          tone.surface,
          tone.line,
        )}
      >
        <span className='flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-400/10 dark:text-primary-300'>
          <PhotographIcon className='h-5 w-5' />
        </span>
        <span className='min-w-0 flex-1'>
          <span className={clsxm('block text-sm font-semibold', tone.ink)}>
            {t('Allow image attachments')}
          </span>
          <span className={clsxm('block text-xs leading-5', tone.quiet)}>
            {t('Customers can send photos and screenshots')}
          </span>
        </span>
        <span className='relative h-6 w-11 flex-none rounded-full bg-primary-600'>
          <span className='absolute end-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow' />
        </span>
      </div>

      <div className='space-y-2.5 rounded-[22px] bg-[linear-gradient(165deg,#d5e7b4_0%,#b4d8c7_45%,#c2d3ee_100%)] p-4 text-[13.5px] leading-[1.4] text-[#000] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)] dark:bg-[linear-gradient(165deg,#0f1c26_0%,#14232f_55%,#101b29_100%)] dark:text-white'>
        <div className='flex justify-end'>
          <div className='w-[76%] overflow-hidden rounded-[14px] rounded-ee-[4px] bg-[#e1ffc7] shadow-[0_1px_1px_rgba(0,0,0,0.12)] dark:bg-[#2b5278]'>
            <div className='p-[3px] pb-0'>
              <RatingPlate />
            </div>
            <p className='px-2.5 pb-2 pt-1.5'>{question}</p>
          </div>
        </div>
        <div
          className={clsxm(styles.rv, 'flex items-end gap-2')}
          style={delay(350)}
        >
          <span
            className={clsxm(
              'flex h-7 w-7 flex-none items-center justify-center rounded-full text-[12px] font-semibold text-white',
              brand.bg,
            )}
          >
            {brand.initial}
          </span>
          <p className='max-w-[80%] rounded-[14px] rounded-es-[4px] bg-white px-2.5 py-1.5 shadow-[0_1px_1px_rgba(0,0,0,0.12)] dark:bg-[#182533]'>
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

/** The silver rating plate on the back of an appliance, photographed. */
function RatingPlate() {
  return (
    <div className='relative h-32 overflow-hidden rounded-[11px] bg-[linear-gradient(135deg,#9aa3ab_0%,#c7cdd2_40%,#a3abb2_100%)] p-3'>
      <div
        dir='ltr'
        className='relative h-full -rotate-2 rounded-md bg-[linear-gradient(160deg,#eef0f2,#d5d9dd)] p-2.5 font-mono text-[10px] leading-[1.35] text-[#30363b] shadow-[0_6px_14px_-6px_rgba(0,0,0,0.45)]'
      >
        <p className='text-[11px] font-bold tracking-wide'>VOLTLINE</p>
        <p>MODEL VX-8200</p>
        <p>230 V ~ 50 Hz · 2100 W</p>
        <div className='absolute bottom-2.5 end-2.5 flex h-6 items-end gap-[2px]'>
          {[3, 1, 2, 1, 3, 2, 1, 1, 3, 1, 2, 3, 1, 2].map((width, index) => (
            <span
              key={index}
              className='h-full bg-[#30363b]'
              style={{ width }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
